import { useConfirm } from "@/components/shared/ConfirmProvider";
import { Head, Link, useForm, router } from "@inertiajs/react";
import { useState } from "react";
import { ArrowLeft, Check, X, UserCheck } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import SignupDetails from "@/components/admin/SignupDetails";
import { FormError } from "@/components/admin/CampaignPlanFields";
import StatusBadge from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

export default function Show({ application }) {
  const [rejecting, setRejecting] = useState(false);

  const approveForm = useForm({});
  const rejectForm = useForm({ reason: "" });

  const confirm = useConfirm();

  const handleApprove = async () => {
    const ok = await confirm({
      title: `Create ${application.school_name}'s account?`,
      body: `This creates the school and emails ${application.applicant_email} a sign-in link. It can't easily be undone.`,
      confirmLabel: "Create account",
    });
    if (ok) approveForm.post(application.approve_path, { preserveState: "errors" });
  };

  const impersonate = async () => {
    const ok = await confirm({
      title: `Sign in as ${application.applicant_name}?`,
      body: `You'll see PagePledge as ${application.applicant_email} does until you stop impersonating.`,
      confirmLabel: "Sign in as them",
    });
    if (ok) router.post(application.impersonate_path);
  };

  const handleReject = (e) => {
    e.preventDefault();
    rejectForm.post(application.reject_path, {
      onSuccess: () => setRejecting(false),
    });
  };

  return (
    <AdminLayout
      eyebrow="Signup answers"
      title={application.school_name}
      actions={
        <Button variant="outline" size="sm" asChild>
          <Link href={application.back_path}>
            <ArrowLeft className="h-4 w-4" />
            {application.approved_school ? "Back to school" : "Back to list"}
          </Link>
        </Button>
      }
    >
      <Head title={`Admin · ${application.school_name}`} />

      {/* ── Status banner ──────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          {application.status !== "approved" && <StatusBadge status={application.status} />}
          <span className="text-sm text-muted-foreground">
            Submitted {application.submitted_ago}
          </span>
        </div>
      </div>

      {application.status === "approved" && application.approved_school && (
        <Alert variant="success" className="mb-6">
          <Check className="h-4 w-4" />
          <AlertTitle>Account created</AlertTitle>
          <AlertDescription>
            School account:{" "}
            <Link href={application.approved_school.admin_path} className="font-semibold underline underline-offset-4">
              {application.approved_school.name}
            </Link>
            {application.reviewed_by && ` — by ${application.reviewed_by}`}.
          </AlertDescription>
        </Alert>
      )}

      {application.impersonate_path && (
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={impersonate}
          >
            <UserCheck className="h-4 w-4" />
            Impersonate {application.applicant_name}
          </Button>
        </div>
      )}

      {application.status === "rejected" && (
        <Alert variant="destructive" className="mb-6">
          <X className="h-4 w-4" />
          <AlertTitle>Rejected</AlertTitle>
          <AlertDescription>
            {application.review_notes || "No reason recorded."}
            {application.reviewed_by && ` — by ${application.reviewed_by}`}.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <SignupDetails application={application} />

        {/* ── Right column: review actions ────────────── */}
        <div>
          {application.status === "pending" ? (
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle>Review</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!rejecting ? (
                  <>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      This application predates instant signup. Approving creates the school's account; the school then sets up its own campaigns, and you approve each one on the school's admin page once it's paid.
                    </p>
                    <FormError message={approveForm.errors.base} />
                    <Button className="w-full" onClick={handleApprove} disabled={approveForm.processing}>
                      <Check className="h-4 w-4" />
                      {approveForm.processing ? "Creating…" : "Create account and invite"}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setRejecting(true)}
                    >
                      <X className="h-4 w-4" />
                      Reject
                    </Button>
                  </>
                ) : (
                  <form onSubmit={handleReject} className="space-y-3">
                    <label htmlFor="reason" className="text-sm font-medium">Reason (internal, not emailed)</label>
                    <textarea
                      id="reason"
                      rows={3}
                      value={rejectForm.data.reason}
                      onChange={(e) => rejectForm.setData("reason", e.target.value)}
                      className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      placeholder="Honeypot tripped, unable to verify, etc."
                    />
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        variant="destructive"
                        className="flex-1"
                        disabled={rejectForm.processing}
                      >
                        Confirm reject
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setRejecting(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle>{application.approved_school ? "Account created" : "Already reviewed"}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {application.approved_school ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      The school sets up its own campaigns. Approve each one on the school's page once its fee is paid.
                    </p>
                    <Button variant="outline" className="w-full" asChild>
                      <Link href={application.approved_school.admin_path}>Open {application.approved_school.name}</Link>
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    This application is in a terminal state and can't be re-reviewed.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

